import { Injectable } from '@angular/core';

const BASE_URL = 'https://hmsqa.dhanushinfotech.com';

export interface TranscriptionOptions { useDiarization?: boolean; translateToEnglish?: boolean; language?: string | null; modelSize?: string; }
export interface TranscriptionResult { language: string; full_text: string; segments: Array<{ start: number; end: number; text: string; speaker?: string; confidence?: number; }>; has_diarization?: boolean; total_speakers?: number; audio_duration?: number; }
export interface SoapResponse { note: string; }
export interface PrescriptionResponse { patient_name: string; /* ...rest omitted for brevity*/ }
export interface CareAdviceResponse { medications: string[]; precautions: string[]; disclaimer: string; error?: string; }

export type PartQuery = { svg_label: string; path_hint?: string[]; free_addition?: string; context?: string };
export type PartResponse = { part_name: string; subparts: string[]; normalized: Record<string, any>; ui_suggestions: Record<string, any> };
export type ComplaintSchemaRequest = { part_name: string; subpart_name?: string };
export type ComplaintSchemaResponse = { fields: any[] };
export type ICDRequest = { part_name: string; subpart_name?: string; title?: string; notes?: string; onset?: string; duration?: string; severity?: number; laterality?: 'left'|'right'|'both'; history?: string[]; external_factors?: string[]; age?: number; };
export type ICDResponse = { primary_code?: string; codes: any[] };

@Injectable({ providedIn: 'root' })
export class AudiofileService {
  
  async parts(body: PartQuery): Promise<PartResponse> {
    const res = await fetch(`${BASE_URL}/AIPyService/aidynamic/parts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    if (!res.ok) throw new Error(`parts HTTP ${res.status}`);
    return res.json();
  }

  async complaintSchema(body: ComplaintSchemaRequest): Promise<ComplaintSchemaResponse> {
    const res = await fetch(`${BASE_URL}/AIPyService/aidynamic/complaint_schema`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    if (!res.ok) throw new Error(`schema HTTP ${res.status}`);
    return res.json();
  }

  async icd(body: ICDRequest): Promise<ICDResponse> {
    const res = await fetch(`${BASE_URL}/AIPyService/aidynamic/icd`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    if (!res.ok) throw new Error(`icd HTTP ${res.status}`);
    return res.json();
  }

  async transcribe(file: File, opts?: TranscriptionOptions): Promise<TranscriptionResult> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('use_diarization', String(!!opts?.useDiarization));
    formData.append('translate_to_english', String(opts?.translateToEnglish ?? true));
    if (opts?.language) formData.append('language', opts.language);
    formData.append('model_size', opts?.modelSize ?? 'small');

    const res = await fetch(`${BASE_URL}/AIPyService/transcribe`, {
      method: 'POST',
      body: formData
    });
    if (!res.ok) throw new Error(`transcribe HTTP ${res.status}`);
    return res.json();
  }

  async generateSoap(payload: any): Promise<SoapResponse> {
    const res = await fetch(`${BASE_URL}/AIPyService/aidynamic/soap/combine-summarize`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    if (!res.ok) throw new Error(`soap HTTP ${res.status}`);
    return res.json();
  }

  async generatePrescription(payload: { transcript: string; keys: string[] }): Promise<PrescriptionResponse> {
    const res = await fetch(`${BASE_URL}/AIPyService/transcribe/prescription`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    if (!res.ok) throw new Error(`prescription HTTP ${res.status}`);
    return res.json();
  }

  async generateCareAdvice(transcript: string): Promise<CareAdviceResponse> {
    const formData = new FormData();
    formData.append('transcript', transcript);
    const res = await fetch(`${BASE_URL}/AIPyService/care/care-advice`, {
      method: 'POST',
      body: formData
    });
    if (!res.ok) throw new Error(`care advice HTTP ${res.status}`);
    return res.json();
  }

  async prubloading(payload: { transcript: string; keys: string[] }) {
    const res = await fetch(`${BASE_URL}/AIPyService/forms/extract`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    if (!res.ok) throw new Error(`prubloading HTTP ${res.status}`);
    return res.json();
  }

  async audit(entityType: 'soap' | 'prescription', action: string, beforeJson?: string, afterJson?: string, entityId?: string, userId?: string) {
    const formData = new FormData();
    formData.append('entity_type', entityType);
    formData.append('action', action);
    if (beforeJson) formData.append('before_json', beforeJson);
    if (afterJson) formData.append('after_json', afterJson);
    if (entityId) formData.append('entity_id', entityId);
    if (userId) formData.append('user_id', userId);

    const res = await fetch(`${BASE_URL}/AIPyService/audit`, {
      method: 'POST',
      body: formData
    });
    if (!res.ok) throw new Error(`audit HTTP ${res.status}`);
    return res.json();
  }
}
